import { action, observable, makeObservable } from 'mobx'
import { v4 as uuidv4 } from 'uuid'
import i18n from 'i18next'
import * as R from 'ramda'
import html2canvas from 'html2canvas'

import { download } from '../../utils/helpers'

class CerticationStore {
    @observable isLoading = true

    @observable automatic = []

    @observable selfAssessed = []

    numberOfRules = 0

    numberOfPassedRules = 0

    @observable isCertified = false

    canvasRef

    iFrameDoc

    @observable showLinkedIn = false

    @observable manualResponseRate = undefined

    @observable certificationStatus

    @observable kpiModalFunc = undefined

    @observable includedKpis = observable.map({ OSS: true, OS: true, NPS: true })

    @observable oss = undefined

    @observable os = undefined

    @observable nps = undefined

    @observable showLinkedinWarning = false

    constructor(store) {
        this.store = store
        makeObservable(this)
    }

    mapStandard = (standard) => {
        switch (standard.type) {
            case 'selfSelectQuestions':
                return standard.selfSelectQuestions.map(({ value, passed }) => ({
                    label: value,
                    passed,
                    id: uuidv4(),
                }))
            case 'responseRateCampaign': {
                const { surveyCompletions, responseRates } = standard
                return Object.assign(standard, {
                    passed: this.manualResponseRate
                        ? this.hasEnoughCompletions(surveyCompletions, this.manualResponseRate, responseRates)
                        : false,
                })
            }
            default:
                return standard
        }
    }

    certifiateAsURL = async () => {
        const hiddenDiv = this.iFrameDoc.getElementById('captureDiv')

        const canvas = await html2canvas(hiddenDiv, { useCORS: true, allowTaint: true })

        return canvas.toDataURL('image/png')
    }

    download = async () => {
        const { isCertified } = this

        if (!isCertified) return

        const url = await this.certifiateAsURL()
        download(url, `Certificate - ${this.getEventName()}.png`)
    }

    @action
    selfCertify = (passed, index) => {
        const selfAssessed = this.selfAssessed.slice()
        selfAssessed[index].passed = passed

        this.selfAssessed = selfAssessed
        this.setPassedRules()
    }

    hasEnoughCompletions = (completions, sends, responseRates) => {
        const [, limit] = responseRates.find(([rate]) => rate >= sends)
        return completions >= limit
    }

    getEventName = () => {
        const { store } = this
        const { survey } = store

        return survey.content.titleByLocale(survey.metadata.defaultLocale)
    }

    @action
    setResponseRate = (rate, index) => {
        this.manualResponseRate = rate
        const automatic = this.automatic.slice()
        const { surveyCompletions, responseRates } = automatic[index]
        automatic[index].passed = !Number.isNaN(rate)
            ? this.hasEnoughCompletions(surveyCompletions, rate, responseRates)
            : false

        this.automatic = automatic
        this.setPassedRules()
    }

    @action
    setPassedRules = () => {
        const { automatic, selfAssessed } = this

        this.numberOfPassedRules = (
            automatic.filter(({ passed }) => passed).length
            + selfAssessed.filter(({ passed }) => passed).length
        )

        this.isCertified = this.numberOfPassedRules === this.numberOfRules
    }

    @action
    getCertification = async () => {
        const { store } = this
        const { survey } = store

        this.automatic = []
        this.selfAssessed = []
        this.isCertified = false
        this.responseRates = []

        try {
            const surveyCertification = await store.http.get(`/certification/survey/${survey.id}`)
            const responseRate = R.pathOr(undefined, ['certificationData', 'responseRateCampaign'], surveyCertification)
            this.manualResponseRate = responseRate
            const certificationStatus = R.pathOr('uncertified', ['status'], surveyCertification)
            this.certificationStatus = certificationStatus

            const certificationData = await store.http.get(`/certification/certify/${survey.id}`)

            const { automatic, selfAssessed } = certificationData.reduce((standards, standard) => {
                const { type } = standard

                const parentMap = (type === 'selfSelectQuestions')
                    ? standards.selfAssessed
                    : standards.automatic

                parentMap.push(this.mapStandard(standard))
                return standards
            }, {
                automatic: [],
                selfAssessed: [],
            })

            this.automatic = automatic
            this.selfAssessed = selfAssessed.flat()
            this.numberOfRules = this.automatic.length + this.selfAssessed.length
            this.setPassedRules()

            this.isLoading = false
        } catch (e) {
            console.error('getCertification', e)
            store.addNotification(null, 'Error certifying survey', 'error')
        }
    }

    /** LinkedIn */
    getURNLinkedIn = async (code) => {
        const { store } = this
        return store.http.get(`/integration/linkedin/urn?code=${code}`)
    }

    onCloseLinkedIn = () => {
        this.showLinkedIn = false
    }

    createPostLinkedIn = async (comment, urn, token) => {
        const { store } = this
        try {
            const image = await this.certifiateAsURL()
            await store.http.post(
                '/integration/linkedin/post',
                JSON.stringify({
                    comment,
                    urn,
                    image,
                    code: token,
                }),
            )
            store.addNotification(null, i18n.t('certification.modal.success'), 'success')
        } catch (e) {
            console.error(e)
            store.addNotification(null, i18n.t('certification.modal.failure'), 'error')
        }
    }
    /** end LinkedIn */

    updateSurveyCertification = async (cb) => {
        const { store } = this
        const { survey, http } = store
        const { id } = survey

        const body = {
            responseRateCampaign: this.manualResponseRate,
            selfCertified: this.selfAssessed.filter((sa) => (!!sa.passed)).map((sa) => (sa.label)),
        }

        const response = await http.post(`/certification/survey/${id}`, JSON.stringify(body))

        this.certificationStatus = response.certificationStatus
        if (cb) cb()
    }

    standardIcon = (standard) => {
        const { store, manualResponseRate } = this
        const { noOfResponses } = store

        if (standard.passed) return 'fal fa-circle-check'

        if (['sendThroughPlatform', 'questions'].includes(standard.type) && !noOfResponses) return 'fa-regular fa-circle-question'

        if (standard.type === 'sentTroughExplori' && !standard.responsesReceived) return 'fa-regular fa-circle-question'

        if (standard.type === 'fieldwork' && standard.status === 'unknown') return 'fa-regular fa-circle-question'

        if (standard.type === 'responseRateCampaign' && manualResponseRate === undefined) {
            return 'fa-regular fa-circle-question'
        }

        return 'fal fa-times-circle'
    }

    toggleShowKpiModal = (func) => {
        this.kpiModalFunc = func
    }

    toggleShowLinkedinWarning = () => {
        this.showLinkedinWarning = !this.showLinkedinWarning
    }

    @action
    setIncludedKpis = (key, value) => {
        this.includedKpis.set(key, value)
    }

    kpiModalSubmission = () => {
        if (this.kpiModalFunc === 'download') {
            this.download()
        }
        if (this.kpiModalFunc === 'linkedin') {
            this.toggleShowLinkedinWarning()
        }

        this.toggleShowKpiModal(undefined)
    }

    getKpiData = async () => {
        const { survey } = this.store
        const { productId } = R.pathOr({}, ['metadata', 'productInfo'], survey)
        const { industry, productType, endDate } = R.pathOr({}, ['metadata', 'productInfo', 'industrySpecificInfo'], survey)
        const { respondentType, surveyType } = R.pathOr({}, ['metadata', 'taxonomy'], survey)
        const endYear = new Date(endDate).getFullYear()
        const widgetDefault = {
            industry,
            productType,
            surveyType,
            respondentType,
            questionId: '00000000-0000-0000-0000-000000000000',
            scope: 'product',
            benchmarkType: 'YOY',
            productId,
            groupId: '1',
            tagOptions: [],
        }

        const body = {
            widget: ['OSS', 'OS', 'NPS'].map((rt) => ({ ...widgetDefault, responseType: rt })),
        }
        const data = await this.store.http.post('/v2/dashboard', JSON.stringify(body))

        this.oss = R.pathOr(undefined, ['data'], data.widget[0].data.find((d) => d.year === endYear))
        this.os = R.pathOr(undefined, ['data'], data.widget[1].data.find((d) => d.year === endYear))
        this.nps = R.pathOr(undefined, ['data'], data.widget[2].data.find((d) => d.year === endYear))
    }
}

export default CerticationStore
